// TASK-683 — the one place a derivation chain is rendered.
//
// Six surfaces improvise provenance today because there was no shared renderer. This is it:
// the canvas "Why this?" popover and TaskCard both feed it the same shape, so a citation
// looks and behaves the same wherever the RM meets it.
//
// Grounding rule: a step without a stored excerpt is not rendered. There is no placeholder,
// no "source unavailable", no synthesised quote — an absent citation is absent.
import { useState } from 'react';
import { FileText, Mic, PenLine, Quote, Sprout } from 'lucide-react';
import { Link } from 'react-router-dom';
import TranscriptEvidencePanel from './TranscriptEvidencePanel';

export interface ProvenanceStep {
  /** The sentence the claim came from, verbatim from the source. Required — no excerpt, no step. */
  excerpt: string;
  /**
   * Where in the source: {page, line, char_span} for documents, {transcript_line} for
   * speech, {sheet, row, column} for spreadsheet uploads.
   */
  locator?: {
    page?: number;
    line?: number;
    char_span?: number[];
    transcript_line?: number;
    sheet?: string;
    row?: number;
    column?: string;
  } | null;
  /** What the claim is, in the graph's own vocabulary (e.g. "employment.employer_name"). */
  fieldPath?: string;
  /** The value derived from the excerpt. */
  value?: unknown;
  /** How many independent observations support it. 1 = single sighting; omitted = not counted. */
  observationCount?: number | null;
  /** Which pipeline recorded it: document | meeting | dictation | manual | seed. */
  origin?: string | null;
  /** Human-facing label for the source (a filename, a meeting title). */
  sourceLabel?: string | null;
  /** Deep link to the source at this locator, when one exists. */
  href?: string | null;
  /**
   * TASK-683 review: a meeting claim has no document to link to, so its quote used to be a
   * dead end — and every cited claim in the graph today is meeting-sourced. These open the
   * transcript at the utterance instead.
   */
  sessionId?: string | null;
  utteranceSeq?: number | null;
  /** A human confirmed this claim. */
  verified?: boolean;
  /** Extraction confidence, 0–1. */
  confidence?: number | null;
}

const ORIGIN_ICON: Record<string, typeof FileText> = {
  document: FileText,
  meeting: Mic,
  dictation: Mic,
  manual: PenLine,
  seed: Sprout,
};

/** "p.2 · line 7" / "transcript line 12" / "Sheet1 · row 2 · Nationality" — only what was
 *  actually recorded. */
export function formatLocator(locator: ProvenanceStep['locator']): string | null {
  if (!locator) return null;
  if (typeof locator.transcript_line === 'number') return `transcript line ${locator.transcript_line}`;
  // TASK-682 review: spreadsheet claims. A CSV has no sheet, so it is omitted rather than
  // named "Sheet1" — the RM opening the file would look for a tab that does not exist.
  if (typeof locator.row === 'number' && locator.column) {
    const cell = [locator.sheet, `row ${locator.row}`, locator.column].filter(Boolean);
    return cell.join(' · ');
  }
  const parts: string[] = [];
  if (typeof locator.page === 'number') parts.push(`p.${locator.page}`);
  if (typeof locator.line === 'number') parts.push(`line ${locator.line}`);
  return parts.length ? parts.join(' · ') : null;
}

function renderValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return null;
  if (typeof value === 'object') {
    const text = (value as { text?: unknown }).text;
    return typeof text === 'string' ? text : null;
  }
  return null;
}

interface ProvenanceChainProps {
  steps: ProvenanceStep[];
  /** What the surface did with these claims — the last link in "source → claim → use". */
  derivedLabel?: string;
  className?: string;
}

export function ProvenanceChain({ steps, derivedLabel, className }: ProvenanceChainProps) {
  const [transcript, setTranscript] = useState<{ sessionId: string; seq?: number } | null>(null);
  const cited = steps.filter((step) => step.excerpt && step.excerpt.trim());
  if (!cited.length) return null;

  return (
    <>
    <ol data-testid="provenance-chain" className={className}>
      {cited.map((step, index) => {
        const Icon = ORIGIN_ICON[step.origin ?? ''] ?? Quote;
        const locatorLabel = formatLocator(step.locator);
        const valueLabel = renderValue(step.value);
        const sourceLabel = step.sourceLabel || step.origin || 'source';
        const sourceLine = [sourceLabel, locatorLabel].filter(Boolean).join(' · ');
        // A spoken claim opens the transcript around the utterance; a document claim opens
        // the file at the page. Neither addressable => plain text, never a dead link.
        const openTranscript = step.sessionId
          ? () => setTranscript({ sessionId: step.sessionId!, seq: step.utteranceSeq ?? undefined })
          : null;

        return (
          <li
            key={`${step.fieldPath ?? 'step'}-${index}`}
            data-testid="provenance-step"
            className="border-t border-border pt-2 first:border-t-0 first:pt-0 [&+li]:mt-2"
          >
            {/* 1. the source sentence, verbatim */}
            <p className="flex gap-1.5 text-xs italic text-foreground">
              <Icon className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground" aria-hidden />
              <span data-testid="provenance-excerpt">“{step.excerpt}”</span>
            </p>

            {/* 2. where it came from — a link when the source can be opened at that spot */}
            <p className="mt-0.5 pl-4 text-[10px] text-muted-foreground">
              {step.href ? (
                <Link
                  to={step.href}
                  data-testid="provenance-source-link"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  {sourceLine}
                </Link>
              ) : openTranscript ? (
                <button
                  type="button"
                  onClick={openTranscript}
                  data-testid="provenance-source-link"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  {sourceLine}
                </button>
              ) : (
                <span data-testid="provenance-source">{sourceLine}</span>
              )}
            </p>

            {/* 3. the claim it produced, and how well supported it is */}
            {(step.fieldPath || valueLabel) && (
              <p className="mt-1 pl-4 text-[11px] text-muted-foreground">
                <span aria-hidden>→ </span>
                {step.fieldPath && <span className="font-medium text-foreground">{step.fieldPath}</span>}
                {valueLabel && <span>{step.fieldPath ? ': ' : ''}{valueLabel}</span>}
              </p>
            )}

            <p className="mt-0.5 flex flex-wrap gap-2 pl-4 text-[10px] text-muted-foreground">
              {typeof step.observationCount === 'number' && (
                <span data-testid="provenance-observations">
                  {step.observationCount === 1
                    ? 'seen once'
                    : `corroborated ${step.observationCount}×`}
                </span>
              )}
              {typeof step.confidence === 'number' && (
                <span data-testid="provenance-confidence">
                  {Math.round(step.confidence * 100)}% confidence
                </span>
              )}
              {step.verified && <span data-testid="provenance-verified">confirmed by a human</span>}
            </p>
          </li>
        );
      })}

      {derivedLabel && (
        <li className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">
          <span aria-hidden>→ </span>
          {derivedLabel}
        </li>
      )}
    </ol>
    {transcript && (
      <TranscriptEvidencePanel
        meetingId={transcript.sessionId}
        utteranceSeq={transcript.seq}
        onClose={() => setTranscript(null)}
      />
    )}
    </>
  );
}

export default ProvenanceChain;
